import {useEffect,useState,type CSSProperties} from 'react';
import {Inspector} from '../../components/os/Inspector';
import {providerMarketApi} from '../../lib/providerMarketApi';
import {freshStepUpProof} from '../../lib/identityAccessApi';
import {useProviderText} from './providerText';
import {providerWizardMessage,type WizardMessageKey,type WizardParams} from './ProviderSetupMessages';
import {centerApi} from './centerApi';
import {
  providerModelLifecycleApi,
  type LifecycleEntry,
  type BatchResult,
} from './providerModelLifecycleApi';
import {
  lifecycleMessage,
  lifecycleStageLabel,
  lifecycleAccuracyLabel,
  lifecycleErrorText,
  type LifecycleMessageKey,
  type LifecycleParams,
} from './providerModelLifecycleMessages';

type CatalogModel=Awaited<ReturnType<typeof providerMarketApi.catalogModels>>['models'][number];
type Props={
  initialProvider:string;
  initialName:string;
  onClose:()=>void;
  onChanged:()=>void;
  onManageModel:(provider:string,model:string)=>void;
};

type StageFilter='ALL'|'SELECTED'|'PREPARED'|'ENABLED'|'ISSUES'|'NO_GATEWAY';

const useCompact=()=>{
  const [compact,setCompact]=useState(()=>
    typeof window!=='undefined'&&window.matchMedia('(max-width: 920px)').matches);
  useEffect(()=>{
    const query=window.matchMedia('(max-width: 920px)');
    const handler=(event:MediaQueryListEvent)=>setCompact(event.matches);
    query.addEventListener('change',handler);
    return()=>query.removeEventListener('change',handler);
  },[]);
  return compact;
};

const sheetStyle=(compact:boolean):CSSProperties=>compact?{
  position:'fixed',inset:0,zIndex:60,overflowY:'auto',overscrollBehavior:'contain',
  background:'var(--surface, #fff)',padding:'56px 12px 130px',WebkitOverflowScrolling:'touch',
}:{
  padding:'0 0 64px',
};

const rowStyle:CSSProperties={
  borderBottom:'1px solid var(--border, #66708544)',display:'flex',flexWrap:'wrap',gap:8,padding:'10px 0',
};

const barStyle=(compact:boolean):CSSProperties=>({
  position:compact?'fixed':'sticky',left:0,right:0,bottom:0,zIndex:70,
  background:'var(--surface-strong, #fff)',borderTop:'1px solid var(--border, #66708566)',
  padding:compact?'12px 16px calc(12px + env(safe-area-inset-bottom))':'10px 16px',
  display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',justifyContent:'space-between',
});

const chipStyle=(active:boolean):CSSProperties=>({
  cursor:'pointer',fontSize:12,lineHeight:'30px',padding:'0 12px',height:32,
  borderRadius:16,border:'1px solid var(--border, #66708566)',
  background:active?'var(--accent-soft, #eef2ff)':'transparent',
  color:'var(--fg, #101828)',whiteSpace:'nowrap',
});

export function ProviderSetupWizard({initialProvider,initialName,onClose,onChanged,onManageModel}:Props){
  const {locale}=useProviderText();
  const t=(key:WizardMessageKey,parameters?:WizardParams)=>providerWizardMessage(locale,key,parameters);
  const lt=(key:LifecycleMessageKey,parameters?:LifecycleParams)=>lifecycleMessage(locale,key,parameters);
  const compact=useCompact();

  const creating=!initialProvider;
  const [providerKey,setProviderKey]=useState(initialProvider);
  const [displayName,setDisplayName]=useState(initialName);
  const [baseUrl,setBaseUrl]=useState('');
  const [apiKey,setApiKey]=useState('');
  const [reason,setReason]=useState(t('defaultReason'));
  const [step,setStep]=useState<'credential'|'catalog'>('credential');
  const [catalog,setCatalog]=useState<CatalogModel[]>([]);
  const [stages,setStages]=useState<Record<string,LifecycleEntry>>({});
  const [stageLoading,setStageLoading]=useState('');
  const [selected,setSelected]=useState<string[]>([]);
  const [selectedMissing,setSelectedMissing]=useState<string[]>([]);
  const [ackSafeProfile,setAckSafeProfile]=useState(false);
  const [expanded,setExpanded]=useState<string|null>(null);
  const [stageFilter,setStageFilter]=useState<StageFilter>('ALL');
  const [search,setSearch]=useState('');
  const [message,setMessage]=useState('');
  const [error,setError]=useState('');
  const [busy,setBusy]=useState('');
  const [lastTest,setLastTest]=useState('');
  const [batch,setBatch]=useState<BatchResult|null>(null);
  const [lastPrepared,setLastPrepared]=useState<string|null>(null);
  const [technicalOpen,setTechnicalOpen]=useState(false);
  const [technicalCode,setTechnicalCode]=useState<string|null>(null);
  const [prepareContext,setPrepareContext]=useState('');
  const [prepareOutput,setPrepareOutput]=useState('');
  const [safeConfirm,setSafeConfirm]=useState<string|null>(null);

  const stage=(model:CatalogModel):LifecycleEntry|null=>stages[model.upstream_model]||null;
  const stageText=(model:CatalogModel):string=>lifecycleStageLabel(locale,stage(model)?.stage||'DISCOVERED');
  const accuracyText=(model:CatalogModel):string|null=>
    lifecycleAccuracyLabel(locale,stage(model)?.metadata?.accuracy);

  const visible=catalog.filter(m=>{
    if(m.catalog_state!=='ACTIVE')return false;
    const haystack=(m.upstream_model+' '+(m.display_name||'')).toLowerCase();
    if(search&&!haystack.includes(search.toLowerCase()))return false;
    const entry=stage(m);
    const hasEntry=m.model_key||stage(m)?.model_key;
    switch(stageFilter){
      case 'SELECTED':return hasEntry?selected.includes(m.model_key||stage(m)?.model_key||''):selectedMissing.includes(m.upstream_model);
      case 'PREPARED':return entry?.stage==='PREPARED';
      case 'ENABLED':return entry?.stage==='ENABLED'||entry?.stage==='AVAILABLE_IN_HARNESS';
      case 'NO_GATEWAY':return !m.model_key&&entry?.stage!=='UNSUPPORTED';
      case 'ISSUES':return entry?.stage==='UNSUPPORTED'||(entry?.metadata?.accuracy==='SAFE_WORKING_PROFILE');
      default:return true;
    }
  });
  const published=visible.filter(m=>!!m.model_key);
  const changes=catalog.filter(m=>{
    if(m.catalog_state!=='ACTIVE'||!m.model_key)return false;
    return selected.includes(m.model_key)!==!!m.selectable;
  });

  const work=async(label:string,fn:()=>unknown)=>{
    if(busy)return;
    setBusy(label);setError('');setMessage('');setTechnicalCode(null);
    try{await fn();onChanged();}
    catch(e){
      const raw=e instanceof Error?e.message:'lifecycle_request_failed';
      const code=/^[a-z][a-z0-9_]{2,100}$/.test(raw)?raw:'lifecycle_request_failed';
      setTechnicalCode(code);
      setError(lifecycleErrorText(locale,code));
      setTechnicalOpen(false);
    }
    finally{setBusy('');}
  };

  const loadStages=async(key:string)=>{
    setStageLoading(lt('loadingStage'));
    try{
      const result=await providerModelLifecycleApi.list(key);
      const next:Record<string,LifecycleEntry>={};
      for(const entry of result.models||[])next[entry.upstream_model]=entry;
      setStages(next);
    }finally{setStageLoading('');}
  };

  const loadModels=async(key:string)=>{
    const result=await providerMarketApi.catalogModels(key);
    const models=result.models||[];
    setCatalog(models);
    setSelectedMissing([]);
    setSelected(models.filter(m=>m.catalog_state==='ACTIVE'&&m.selectable&&m.model_key).map(m=>m.model_key!));
    setStep('catalog');
    await loadStages(key);
    return models.length;
  };

  const save=async()=>{
    const key=providerKey.trim().toLowerCase();
    if(!/^[a-z0-9][a-z0-9._-]{1,98}[a-z0-9]$/.test(key))throw new Error(t('keyInvalid'));
    if(apiKey.trim().length<8)throw new Error(t('enterKey'));
    if(reason.trim().length<5)throw new Error(t('reasonShort'));
    try{
      if(creating){
        let url:URL;
        try{url=new URL(baseUrl.trim());}catch{throw new Error(t('invalidUrl'));}
        if(url.protocol!=='https:'||url.username||url.password||url.search||url.hash)throw new Error(t('invalidUrl'));
        if(!displayName.trim())throw new Error(t('nameMissing'));
        const proof=await freshStepUpProof();
        const response=await fetch('/api/v1/provider-market/registry',{
          method:'POST',credentials:'include',headers:{'Content-Type':'application/json','Accept':'application/json'},
          body:JSON.stringify({
            provider_key:key,display_name:displayName.trim(),protocol_adapter:'OPENAI_COMPATIBLE',
            api_base_url:url.toString(),auth_method:'API_KEY',model_discovery_strategy:'OPENAI_LIST',
            timeout_ms:30000,retry_count:3,is_active:true,is_direct_inference:true,metadata:{},
            api_key:apiKey,step_up:proof,
          }),
        });
        if(!response.ok){
          const responseBody=await response.json().catch(()=>null);
          throw new Error(typeof responseBody?.detail==='string'?responseBody.detail:'provider_registration_failed_'+response.status);
        }
      }else{
        const result=await providerMarketApi.saveProviderApiKey({providerKey:key,apiKey,reason:reason.trim()});
        if(!result.updated||!result.result?.saved||result.result.provider_key!==key)
          throw new Error(t('vaultFailed'));
        // Saving a Vault key does not necessarily connect this provider in Provider Market.
        // Verify configured state, not an optional/redacted credential_ref field.
        const current=await centerApi.snapshot();
        const savedProvider=current.providers.find(p=>p.provider_key===key);
        if(savedProvider?.vault_configured!==true)
          throw new Error(t('vaultRefFailed'));
      }
      setProviderKey(key);
      setMessage(t('saved'));
      await loadModels(key);
    }finally{setApiKey('');}
  };

  const testAndDiscover=async()=>{
    const response=await providerMarketApi.testAndSyncProvider(providerKey);
    if(!response.updated||response.result?.state!=='SUCCESS')
      throw new Error(response.result?.reason||t('catalogFailed'));
    const total=await loadModels(providerKey);
    setLastTest('PASS');
    setMessage(t('catalogSuccess',{count:total}));
  };

  const applyPermissions=async()=>{
    if(changes.length>20)throw new Error(t('tooMany'));
    if(reason.trim().length<5)throw new Error(t('reasonShort'));
    const want=changes.filter(m=>selected.includes(m.model_key!));
    const drops=changes.filter(m=>!selected.includes(m.model_key!));
    const installed=want.length?await providerModelLifecycleApi.batch({
      providerKey,action:'ENABLE',modelKeys:want.map(m=>m.model_key!),
      reason:reason.trim(),acceptSafeProfile:false,
    }):null;
    const removed=drops.length?await providerModelLifecycleApi.batch({
      providerKey,action:'DISABLE',modelKeys:drops.map(m=>m.model_key!),
      reason:reason.trim(),acceptSafeProfile:false,
    }):null;
    await loadModels(providerKey);
    const last=removed||installed;
    if(last)setBatch(last);
    const executed=(installed?.executed||0)+(removed?.executed||0);
    const failed=(installed?.failed||0)+(removed?.failed||0);
    setMessage(lt('batchApplied',{ok:executed,fail:failed}));
    if(failed)setError(lt('batchFailedSome'));
  };

  const autoPrepare=async(model:CatalogModel,acceptSafe=false)=>{
    if(reason.trim().length<5)throw new Error(t('reasonShort'));
    try{
      const result=await providerModelLifecycleApi.prepareAuto({
        providerKey,upstreamModel:model.upstream_model,
        reason:reason.trim(),acceptSafeProfile:acceptSafe,
      });
      if(!result.prepared||result.enabled)throw new Error(t('prepareFailed'));
      setSafeConfirm(null);
      await loadModels(providerKey);
      setStageFilter('PREPARED');
      setSearch('');
      setExpanded(model.upstream_model);
      setLastPrepared(model.upstream_model);
      setMessage(lt('autoPrepareDone',{model:model.upstream_model}));
    }catch(e){
      if(e instanceof Error&&e.message==='safe_profile_confirmation_required'){
        setSafeConfirm(model.upstream_model);
        setError(lt('errorSafeProfile'));
        return;
      }
      throw e;
    }
  };

  const prepareSelectedMissing=async()=>{
    if(!ackSafeProfile)throw new Error(lt('confirmBatchSafeFirst'));
    if(!selectedMissing.length||selectedMissing.length>50)throw new Error(lt('selectMissingFirst'));
    if(reason.trim().length<5)throw new Error(t('reasonShort'));
    const result=await providerModelLifecycleApi.batch({
      providerKey,action:'PREPARE_AUTO',upstreamModels:selectedMissing,
      reason:reason.trim(),acceptSafeProfile:true,
    });
    setBatch(result);
    await loadModels(providerKey);
    if(result.executed>0){
      const first=result.results.find(row=>row.status==='EXECUTED');
      const target=typeof first?.target==='string'?first.target:null;
      setStageFilter('PREPARED');
      setSearch('');
      setLastPrepared(target);
      if(target)setExpanded(target);
    }
    setMessage(lt('batchApplied',{ok:result.executed,fail:result.failed}));
    if(result.failed)setError(lt('batchFailedSome'));
  };

  const manualPrepare=async(model:CatalogModel)=>{
    const context=Number(prepareContext);
    const output=Number(prepareOutput);
    if(!Number.isSafeInteger(context)||context<256||context>10_000_000
      ||!Number.isSafeInteger(output)||output<1||output>context)
      throw new Error(t('invalidLimits'));
    if(reason.trim().length<5)throw new Error(t('reasonShort'));
    const result=await providerMarketApi.prepareGatewayModel({
      providerKey,upstreamModel:model.upstream_model,
      contextWindow:context,maxOutputTokens:output,reason:reason.trim(),
    });
    if(!result.updated||!result.result?.prepared||result.result.enabled)
      throw new Error(t('prepareFailed'));
    setPrepareContext('');setPrepareOutput('');
    await loadModels(providerKey);
    setMessage(t('prepared',{model:model.upstream_model}));
  };

  const modelTest=async(model:CatalogModel)=>{
    const response=await centerApi.test(providerKey,model.model_key!,'easy',60000,1,crypto.randomUUID());
    if(response.status!=='PASS')throw new Error(t('testFailed',{status:String(response.status||'UNKNOWN')}));
    setMessage(t('testPassed',{model:model.upstream_model}));
  };

  const toggleSelect=(model:CatalogModel)=>{
    const key=model.model_key||stage(model)?.model_key;
    if(!key)return;
    setSelected(old=>old.includes(key)?old.filter(k=>k!==key):[...old,key]);
  };

  const toast=(text:string,tone:'error'|'status')=><p
    role={tone==='error'?'alert':'status'}>{text}</p>;

  const filters:StageFilter[]=['ALL','SELECTED','PREPARED','ENABLED','NO_GATEWAY','ISSUES'];
  const filterLabel=(value:StageFilter):string=>{
    const map:Record<StageFilter,LifecycleMessageKey>={
      ALL:'filterAll',SELECTED:'filterSelected',PREPARED:'filterPrepared',
      ENABLED:'filterEnabled',ISSUES:'filterIssues',NO_GATEWAY:'filterNoGateway',
    };
    return lt(map[value]);
  };

  const content=<>
    <div className="pmc-form" style={sheetStyle(compact)}>
      <p><strong>{t('stepProvider')}</strong> {'→'} <strong>{t('stepTest')}</strong> {'→'} <strong>{t('stepModels')}</strong> {'→'} <strong>{t('stepHarness')}</strong></p>
      {error&&toast(error,'error')}
      {busy&&toast(busy+'…','status')}
      {message&&toast(message,'status')}
      {lastPrepared&&<div role="status" style={{border:'1px solid var(--border, #66708566)',borderRadius:10,padding:12,marginBottom:12}}>
        <strong>{lt('preparedNextTitle')}</strong>
        <p>{lt('preparedNextBody',{model:lastPrepared})}</p>
        <div className="pmc-actions">
          <button type="button" disabled={!!busy} onClick={()=>{
            setStageFilter('PREPARED');setSearch('');setExpanded(lastPrepared);
          }}>{lt('viewPreparedModel')}</button>
          <button type="button" disabled={!!busy} onClick={()=>{
            const entry=stages[lastPrepared];
            const key=entry?.model_key||catalog.find(m=>m.upstream_model===lastPrepared)?.model_key;
            if(key)onManageModel(providerKey,key);
            else setError(lt('modelNotReadyForManage'));
          }}>{lt('manageModelAccess')}</button>
          <button type="button" onClick={()=>setLastPrepared(null)}>{lt('dismissGuidance')}</button>
        </div>
      </div>}
      {error&&technicalCode&&<details open={technicalOpen} onToggle={e=>setTechnicalOpen(e.currentTarget.open)}>
        <summary style={{cursor:'pointer'}}>{lt('technicalDetails')}</summary>
        <p style={{fontFamily:'monospace',fontSize:12,overflowWrap:'anywhere'}}>{lt('errorCodeLabel')}: {technicalCode}</p>
        <button type="button" onClick={()=>void navigator.clipboard?.writeText('Provider Market | '+technicalCode)}>{lt('copyErrorCode')}</button>
      </details>}
      {stageLoading&&!busy&&toast(stageLoading,'status')}
      <label>{t('providerKey')} <input disabled={!creating||step==='catalog'||!!busy} value={providerKey} onChange={e=>setProviderKey(e.target.value)} placeholder={t('providerKeyHint')}/></label>
      {creating&&<>
        <label>{t('displayName')} <input disabled={step==='catalog'||!!busy} value={displayName} onChange={e=>setDisplayName(e.target.value)} placeholder={t('displayNameHint')}/></label>
        <label>{t('baseUrl')} <input disabled={step==='catalog'||!!busy} type="url" value={baseUrl} onChange={e=>setBaseUrl(e.target.value)} placeholder="https://api.example.com/v1"/></label>
        <p>{t('providerNote')}</p>
      </>}
      {step==='credential'&&<>
        <label>{t('apiKey')} <input type="password" autoComplete="new-password" spellCheck={false} disabled={!!busy} value={apiKey} onChange={e=>setApiKey(e.target.value)} placeholder={t('apiKeyHint')}/></label>
        <label>{t('reason')} <input disabled={!!busy} value={reason} onChange={e=>setReason(e.target.value)}/></label>
        <button className="pmc-primary" disabled={!!busy||apiKey.trim().length<8||reason.trim().length<5} onClick={()=>void work(t('saving'),save)}>{t('save')}</button>
        {!creating&&<button disabled={!!busy} onClick={()=>void work(t('reading'),async()=>{await loadModels(providerKey);})}>{t('alreadySaved')}</button>}
      </>}
      {step==='catalog'&&<>
        <div className="pmc-actions"><button className="pmc-primary" disabled={!!busy} onClick={()=>void work(t('testing'),testAndDiscover)}>{t('testAndRefresh')}</button>
        <button disabled={!!busy} onClick={()=>{setStep('credential');setLastTest('');}}>{t('changeKey')}</button></div>
        <p>{t('connectionTest')} <strong>{lastTest||t('notTested')}</strong>{' '}{t('testWarning')}</p>
        <label>{t('searchModels')} <input inputMode="search" value={search} onChange={e=>setSearch(e.target.value)} placeholder={t('modelName')}/></label>
        <div style={{display:'flex',gap:6,flexWrap:'wrap',padding:'4px 0 8px'}}>
          {filters.map(value=><button key={value} type="button" style={chipStyle(stageFilter===value)} disabled={!!busy} onClick={()=>setStageFilter(value)}>{filterLabel(value)}</button>)}
        </div>
        <p>{t('summary',{active:visible.length,published:published.length,selected:selected.length,changes:changes.length})}</p>
        {visible.map(m=>{
          const key=m.model_key||stage(m)?.model_key||null;
          const isOpen=expanded===m.upstream_model;
          const metadata=stage(m)?.metadata;
          return <div key={m.upstream_model} style={rowStyle}>
            <label style={{flex:1,minWidth:'55%'}}>
              <input type="checkbox" disabled={!!busy||(!key&&stage(m)?.stage==='UNSUPPORTED')}
                checked={key?selected.includes(key):selectedMissing.includes(m.upstream_model)}
                onChange={()=>key?toggleSelect(m):setSelectedMissing(old=>old.includes(m.upstream_model)?old.filter(x=>x!==m.upstream_model):[...old,m.upstream_model])}/>
              {' '}<strong style={{overflowWrap:'anywhere'}}>{m.display_name||m.upstream_model}</strong>
              {' '}({m.cost_class||'UNKNOWN'})
            </label>
            <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
              <span style={{fontSize:12}}>{stageText(m)}</span>
              {accuracyText(m)&&<span style={{fontSize:12}}>{accuracyText(m)}</span>}
              <button type="button" disabled={!!busy} onClick={()=>setExpanded(old=>old===m.upstream_model?null:m.upstream_model)}>{lt('detailShort')}</button>
            </div>
            {isOpen&&<div className="pmc-form" style={{width:'100%',padding:'8px 0 0'}}>
              {!key&&metadata?.accuracy!=='UNSUPPORTED'&&
                (safeConfirm===m.upstream_model&&metadata?.safe_profile
                  ?<div>
                    <p style={{fontSize:13}}>{lt('safeProfilePrompt')}</p>
                    <button type="button" className="pmc-primary" disabled={!!busy}
                      onClick={()=>void work(t('preparing'),async()=>{await autoPrepare(m,true);})}>
                      {lt('safeProfileConfirm')}</button>
                  </div>
                  :<button type="button" disabled={!!busy}
                    onClick={()=>void work(t('preparing'),async()=>{await autoPrepare(m,false);})}>
                    {lt('autoPrepare')}</button>)}
              {key&&<>
                <div className="pmc-actions">
                  <button disabled={!!busy} onClick={()=>onManageModel(providerKey,key!)}>{lt('detailShort')}</button>
                  <button disabled={!!busy||!m.selectable||(m.cost_class!=='FREE'&&providerKey!=='ollama')} title={t('freeOnly')}
                    onClick={()=>void work(t('liveTesting'),async()=>modelTest(m))}>{lt('testModelShort')}</button>
                </div>
              </>}
              <details>
                <summary style={{cursor:'pointer',fontSize:13}}>{lt('advanced')}</summary>
                <p>{t('limitNote')}</p>
                <label>{t('contextLimit')}
                  <input type="number" inputMode="numeric" min="256" max="10000000" step="1" disabled={!!busy||!!key}
                    value={prepareContext} onChange={e=>setPrepareContext(e.target.value)}/>
                </label>
                <label>{t('outputLimit')}
                  <input type="number" inputMode="numeric" min="1" max="10000000" step="1" disabled={!!busy||!!key}
                    value={prepareOutput} onChange={e=>setPrepareOutput(e.target.value)}/>
                </label>
                <button type="button" disabled={!!busy||!!key}
                  onClick={()=>void work(t('preparing'),async()=>{await manualPrepare(m);})}>
                  {t('prepareGatewayConfirm')}</button>
              </details>
              {metadata&&<p style={{fontSize:12}}>
                {lt(metadata.real_capacity_known?'realCapacity':'capacityUnknown')}
                {metadata.real_capacity_known&&': '}
                {metadata.real_capacity_known&&metadata.real_context_window
                  ?metadata.real_context_window.toLocaleString()+' → '+(metadata.real_max_output_tokens||0).toLocaleString()
                  :''}
                {metadata.safe_profile&&' · '+lt('workingLimit')+': '+metadata.context_window?.toLocaleString()+' → '+(metadata.max_output_tokens||0).toLocaleString()}
              </p>}
            </div>}
          </div>;
        })}
        {!visible.length&&<p>{catalog.length?t('noModels'):lt('noModelsAtAll')}</p>}
        {batch&&batch.results.length>0&&
          <details>
            <summary style={{cursor:'pointer'}}>{lt('batchDetail')} ({batch.executed}/{batch.total})</summary>
            <ul style={{fontSize:12,paddingLeft:18}}>
              {batch.results.map((row,i)=><li key={String(row.target)+':'+i}>
                {String(row.target)} — {String(row.status)}
                {row.error?` · ${lifecycleErrorText(locale,String(row.error))}`:''}
                {row.error&&<details><summary style={{cursor:'pointer'}}>{lt('technicalDetails')}</summary>
                  <code>{lt('errorCodeLabel')}: {String(row.error).slice(0,120)}</code>
                </details>}
              </li>)}
            </ul>
          </details>}
        {selectedMissing.length>0&&<div className="pmc-form" style={{border:'1px solid var(--border, #66708566)',padding:12,borderRadius:8}}>
          <p>{lt('selectedMissingCount',{count:selectedMissing.length})}</p>
          <label><input type="checkbox" checked={ackSafeProfile} disabled={!!busy}
            onChange={e=>setAckSafeProfile(e.target.checked)}/>{' '}{lt('batchSafeAcknowledgement')}</label>
          <button type="button" className="pmc-primary"
            disabled={!!busy||!ackSafeProfile||selectedMissing.length>50||reason.trim().length<5}
            onClick={()=>void work(t('preparing'),prepareSelectedMissing)}>
            {lt('prepareInBatch')} ({selectedMissing.length})
          </button>
        </div>}
        <label>{t('permissionReason')} <input disabled={!!busy} value={reason} onChange={e=>setReason(e.target.value)}/></label>
        <button className="pmc-primary" disabled={!!busy||lastTest!=='PASS'||!changes.length||changes.length>20||reason.trim().length<5} onClick={()=>void work(t('applying'),applyPermissions)}>{t('apply',{changes:changes.length})}</button>
        <p>{t('changeWarning')}</p>
      </>}
    </div>
    {step==='catalog'&&<div style={barStyle(compact)}>
      <span style={{fontSize:13}}>{t('summary',{active:visible.length,published:published.length,selected:selected.length,changes:changes.length})}</span>
      <div className="pmc-actions" style={{flexWrap:'nowrap',gap:6}}>
        <button type="button" disabled={!!busy||!visible.length} onClick={()=>{
          const keys=visible.filter(m=>m.model_key).map(m=>m.model_key!);
          const missing=visible.filter(m=>!m.model_key&&stage(m)?.stage!=='UNSUPPORTED').map(m=>m.upstream_model);
          setSelected(old=>[...new Set([...old,...keys])]);
          setSelectedMissing(old=>[...new Set([...old,...missing])]);
        }}>{lt('selectAllFiltered')}</button>
        <button type="button" disabled={!!busy||(!selected.length&&!selectedMissing.length)} onClick={()=>{setSelected([]);setSelectedMissing([]);}}>{lt('clearSelection')}</button>
      </div>
    </div>}
  </>;
  return compact
    ?<div role="dialog" aria-modal="true" aria-label={t('title')}
        style={{position:'fixed',inset:0,zIndex:1000,background:'var(--surface,#fff)'}}>
        <button type="button" disabled={!!busy}
          style={{position:'fixed',right:12,top:8,zIndex:1002,minHeight:40,padding:'0 12px'}}
          onClick={()=>{setApiKey('');onClose();}}>{t('close')}</button>
        {content}
      </div>
    :<Inspector title={t('title')} onClose={()=>{setApiKey('');onClose();}} busy={!!busy}
        modal wide className="pmc-dialog"
        actions={<button type="button" disabled={!!busy} onClick={onClose}>{t('close')}</button>}>
        {content}
      </Inspector>;
}
